import { useEffect, useState, useMemo, type CSSProperties } from "react";
import {
  listSuggestedEdits,
  moderateSuggestedEdit,
  type SuggestedEdit,
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

const TRUST_COLORS: Record<string, { bg: string; color: string }> = {
  high: { bg: "rgba(39,174,96,0.15)", color: "#27ae60" },
  med: { bg: "rgba(249,175,54,0.15)", color: "var(--gold)" },
  low: { bg: "rgba(224,80,64,0.15)", color: "#E05040" },
  none: { bg: "rgba(138,122,102,0.15)", color: "var(--muted)" },
};

export default function AdminSuggestedEdits() {
  const isPhone = useIsMobile(768);
  const [edits, setEdits] = useState<SuggestedEdit[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const [searchQuery, setSearchQuery] = useState("");

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listSuggestedEdits(statusFilter);
      setEdits(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load suggested edits", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const filteredEdits = useMemo(() => {
    if (!searchQuery.trim()) return edits;
    const q = searchQuery.toLowerCase().trim();
    return edits.filter(
      (e) =>
        (e.item_name && e.item_name.toLowerCase().includes(q)) ||
        e.field_name.toLowerCase().includes(q) ||
        (e.suggested_by_name && e.suggested_by_name.toLowerCase().includes(q)) ||
        e.new_value.toLowerCase().includes(q)
    );
  }, [edits, searchQuery]);

  const handleModerate = async (
    edit: SuggestedEdit,
    status: "approved" | "rejected"
  ) => {
    try {
      await moderateSuggestedEdit(
        edit.id,
        status,
        edit.item_id,
        edit.field_name,
        status === "approved" ? edit.new_value : undefined
      );
      showToast(`✓ Marked edit as ${status}`);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to moderate edit", "err");
    }
  };

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="edits" badgeCounts={{ edits: edits.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Community Suggested Edits</h1>
          <p style={styles.subtitle}>
            Review crowd-sourced corrections for whiskey mashbills, age statements, distillery provenance, and proofs.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {(["pending", "approved", "rejected", "all"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              style={statusFilter === tab ? styles.tabBtnActive : styles.tabBtn}
              onClick={() => setStatusFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search by bottle, field name, submitter…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div style={styles.loader}>Loading suggested edits…</div>
      ) : filteredEdits.length === 0 ? (
        <div style={styles.emptyState}>No suggested edits in this queue.</div>
      ) : (
        <div style={styles.editsList}>
          {filteredEdits.map((e) => {
            const trustStyle = TRUST_COLORS[e.submitter_trust || "none"] || TRUST_COLORS.none;
            return (
              <div key={e.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  {/* The title wraps; the badges keep their size (anywhere on the whole header let
                      them shrink to one letter a line). */}
                  <div style={{ flex: "1 1 200px", minWidth: 0, overflowWrap: "anywhere" }}>
                    <h3 style={styles.bottleTitle}>{e.item_name}</h3>
                    <div style={styles.fieldPill}>Field: <strong>{(e.field_name || "unknown").toUpperCase()}</strong></div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, whiteSpace: "nowrap" }}>
                    <span
                      style={{
                        ...styles.trustBadge,
                        background: trustStyle.bg,
                        color: trustStyle.color,
                      }}
                    >
                      Trust: {(e.submitter_trust || "Guest").toUpperCase()}
                    </span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        background:
                          e.status === "approved"
                            ? "rgba(39,174,96,0.15)"
                            : e.status === "rejected"
                            ? "rgba(224,80,64,0.15)"
                            : "rgba(249,175,54,0.15)",
                        color:
                          e.status === "approved"
                            ? "#27ae60"
                            : e.status === "rejected"
                            ? "#E05040"
                            : "var(--gold)",
                      }}
                    >
                      {(e.status || "pending").toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Values Comparison */}
                <div style={isPhone ? { ...styles.comparisonGrid, gridTemplateColumns: "minmax(0, 1fr)" } : styles.comparisonGrid}>
                  <div style={styles.valBox}>
                    <div style={styles.valLabel}>Current Value in Catalog</div>
                    <div style={styles.valTextOld}>{e.old_value || "— (Empty)"}</div>
                  </div>
                  <div style={styles.arrowIcon}>{isPhone ? "↓" : "→"}</div>
                  <div style={styles.valBox}>
                    <div style={styles.valLabel}>Proposed New Value</div>
                    <div style={styles.valTextNew}>{e.new_value}</div>
                  </div>
                </div>

                {e.source_url && (
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                    Source Proof:{" "}
                    <a
                      href={e.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--gold)", overflowWrap: "anywhere" }}
                    >
                      {e.source_url}
                    </a>
                  </div>
                )}

                <div style={styles.cardFooter}>
                  <span>Submitted by <strong>{e.suggested_by_name || "Community Member"}</strong> on {new Date(e.created_at).toLocaleDateString()}</span>
                  {e.status === "pending" && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        style={styles.btnApprove}
                        onClick={() => handleModerate(e, "approved")}
                      >
                        ✓ Approve &amp; Apply
                      </button>
                      <button
                        type="button"
                        style={styles.btnReject}
                        onClick={() => handleModerate(e, "rejected")}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  filterTabs: { display: "flex", gap: 6 },
  tabBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  tabBtnActive: { background: "rgba(249,175,54,0.15)", border: "1px solid var(--gold)", borderRadius: 8, color: "var(--gold)", padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  searchInput: { minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  editsList: { display: "grid", gap: 12 },
  card: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 10 },
  // wrap: on a phone the badges drop under the title instead of squeezing it to one word a line.
  cardHeader: { display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 8 },
  bottleTitle: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" },
  fieldPill: { fontSize: 11, color: "var(--gold)", marginTop: 2 },
  trustBadge: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4 },
  statusBadge: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4 },
  comparisonGrid: { display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "center", background: "var(--bg)", padding: 12, borderRadius: 8, border: "1px solid var(--border)" },
  valBox: { display: "flex", flexDirection: "column", gap: 4, minWidth: 0 },
  valLabel: { fontSize: 10, textTransform: "uppercase", color: "var(--muted)", fontWeight: 700, letterSpacing: 0.5 },
  valTextOld: { fontSize: 13, color: "var(--muted)", overflowWrap: "anywhere" },
  valTextNew: { fontSize: 13, color: "#27ae60", fontWeight: 700, overflowWrap: "anywhere" },
  arrowIcon: { fontSize: 18, color: "var(--gold)", fontWeight: 700 },
  // wrap + anywhere: an email-style submitter name with no spaces widened the card past a phone.
  cardFooter: { display: "flex", flexWrap: "wrap", gap: 8, overflowWrap: "anywhere", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4, fontSize: 11, color: "var(--muted)" },
  btnApprove: { background: "rgba(39,174,96,0.15)", border: "1px solid rgba(39,174,96,0.4)", color: "#27ae60", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnReject: { background: "rgba(224,80,64,0.1)", border: "1px solid rgba(224,80,64,0.3)", color: "#E05040", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
};
